import React from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import { Line, VictoryChart, VictoryAxis, VictoryBar } from 'victory';
import { RELEASE_COLOR_MAP, getDomainForDay, RELEASES } from '../../Timeline/Timeline.helpers';

import { timelineChartAxisX } from '../../Timeline/TimelineChart.parts';
import { ReleaseBar } from '../../Timeline/ReleaseBar';
import { ReleaseOffsetLabelWithTooltip } from '../../Timeline/ReleaseOffsetLabelWithTooltip';
import { TimelineLabel } from '../../Timeline/TimelineLabel';
import { MaintenanceChartRow } from './MaintenanceChartRow';
import { ErrorBoundary } from '../../ErrorBoundary/ErrorBoundary';
import { chartTheme, chartLabelStyles, getGridStroke } from '../../../common/ChartTheme';
import { RoundedBottomBorderNoData, RoundedBottomBorder } from '../Chart.styles';
import { barCornerRadius } from '../Chart.utils';
import { defaultVictoryContainer } from '../Chart.defaults';

export const MaintenanceReleaseChartVictory = ({ timelineData, chartWidth, itemLabelWidth, theme }) => {
    // To keep row height consistenly at (rowHeight), we have to calculate chart height
    const rowHeight = 52;
    const itemHeight = 18;
    const timeAxisHeight = 35;
    const bordersFix = 2;

    const chartPadding = {
        left: itemLabelWidth - bordersFix / 2,
        right: -bordersFix,
        top: timeAxisHeight,
        bottom: timeAxisHeight,
    };
    function uniqueByKey(array) {
        return array.filter((item, i) => array.findIndex((nextItem) => item.key === nextItem.key) === i);
    }

    if (!timelineData) {
        return <RoundedBottomBorderNoData chartWidth={chartWidth} />;
    }

    if (timelineData.length === 0) {
        return <RoundedBottomBorderNoData chartWidth={chartWidth} message="No work orders for selected period" />;
    }

    const timelineRows = uniqueByKey(timelineData).length;
    const { start_time, release_time } = timelineData[0].turbineData;
    const chartDay = moment(release_time || start_time).toDate();
    const calculatedChartHeight = rowHeight * timelineRows + chartPadding.top + chartPadding.bottom;

    return (
        <RoundedBottomBorder>
            <ErrorBoundary>
                <VictoryChart
                    theme={chartTheme}
                    domainPadding={{ x: [-rowHeight / 2, rowHeight / 2] }}
                    height={calculatedChartHeight}
                    width={chartWidth - bordersFix}
                    padding={chartPadding}
                    domain={{
                        y: getDomainForDay(chartDay),
                        x: [0, timelineRows],
                    }}
                    containerComponent={defaultVictoryContainer}>
                    <VictoryAxis
                        key="1"
                        name="entity-axis"
                        orientation="left"
                        tickLabelComponent={
                            <TimelineLabel
                                data={timelineData}
                                width={itemLabelWidth}
                                rowComponent={<MaintenanceChartRow />}
                            />
                        }
                        gridComponent={<Line x1={0} />}
                        fixLabelOverlap
                        style={{
                            tickLabels: chartLabelStyles,
                            grid: {
                                strokeWidth: rowHeight + 1,
                                stroke: getGridStroke(theme),
                            },
                        }}
                    />
                    {timelineChartAxisX({ orientation: 'top', timeAxisHeight, chartDay })}
                    {timelineChartAxisX({
                        orientation: 'bottom',
                        timeAxisHeight,
                        chartDay,
                    })}

                    <VictoryBar
                        horizontal
                        key="1"
                        name="release-chart"
                        dataComponent={
                            <ReleaseBar
                                alignmentFn={(d) => (d.type === RELEASES.HV ? 'start' : 'end')}
                                spaceBetween={2}
                            />
                        }
                        cornerRadius={barCornerRadius}
                        labels={({ datum }) => datum.type}
                        labelComponent={<ReleaseOffsetLabelWithTooltip alignmentOffset={11} />}
                        style={{
                            labels: { fill: 'black', fontSize: 11, padding: 5 },
                            data: {
                                width: itemHeight,
                                fill: ({ datum }) => RELEASE_COLOR_MAP[datum.type],
                                stroke: ({ datum }) => RELEASE_COLOR_MAP[datum.type],
                                strokeWidth: 1,
                                fillOpacity: 0.75,
                            },
                        }}
                        x={(d) => d.key}
                        y={(d) => d.beginDate}
                        y0={(d) => d.endDate}
                        data={timelineData}
                    />
                </VictoryChart>
            </ErrorBoundary>
        </RoundedBottomBorder>
    );
};

MaintenanceReleaseChartVictory.displayName = 'MaintenanceReleaseChartVictory';

MaintenanceReleaseChartVictory.propTypes = {
    timelineData: PropTypes.array,
    turbineData: PropTypes.array,
    chartWidth: PropTypes.number,
    itemLabelWidth: PropTypes.number,
};

MaintenanceReleaseChartVictory.defaultProps = {
    chartWidth: 1890,
    itemLabelWidth: 400,
};
